import { cn } from '@lib/utils';
import * as Switch from '@radix-ui/react-switch';
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';


export interface ToggleProps
  extends Omit<ComponentPropsWithoutRef<typeof Switch.Root>, 'onCheckedChange'> {
  label?: ReactNode;
  description?: ReactNode;
  onCheckedChange?: (checked: boolean) => void;
  containerClassName?: string;
}

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(function Toggle(
  { label, description, className, containerClassName, onCheckedChange, id, ...rest },
  ref,
) {
  const reactId = useId();
  const tid = id ?? reactId;
  return (
    <label htmlFor={tid} className={cn('flex items-start gap-3 cursor-pointer select-none', containerClassName)}>
      <Switch.Root
        ref={ref}
        id={tid}
        onCheckedChange={onCheckedChange}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ease-apple',
          'bg-apple-gray-400',
          'data-[state=checked]:bg-gradient-primary data-[state=checked]:shadow-btn-green',
          'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-link/25',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className,
        )}
        {...rest}
      >
        <Switch.Thumb className="block h-5 w-5 translate-x-0.5 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-transform duration-200 ease-apple data-[state=checked]:translate-x-[22px]" />
      </Switch.Root>

      {(label || description) && (
        <div className="flex flex-col pt-0.5">
          {label ? (
            <span className="text-[14px] font-medium text-apple-label leading-tight">{label}</span>
          ) : null}
          {description ? (
            <span className="mt-0.5 text-[12px] text-apple-gray-600 leading-snug">
              {description}
            </span>
          ) : null}
        </div>
      )}
    </label>
  );
});
